const lazyVideos = new Map();

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const saveData = navigator.connection?.saveData === true;

function syncVideo(video, state) {
  const manual = reduceMotion.matches || saveData;
  video.controls = manual || state.controls;
  if (manual || document.hidden || !state.visible) {
    video.pause();
    return;
  }
  video.preload = 'metadata';
  video.play().catch(() => {});
}

// One observer for the whole page. Decorative reels only run while visible;
// reduced-motion/data-saving visitors can choose to play them with controls.
const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  for (const entry of entries) {
    const state = lazyVideos.get(entry.target);
    if (!state) continue;
    state.visible = entry.isIntersecting;
    syncVideo(entry.target, state);
  }
}, { threshold: 0.08 }) : null;

function prepareVideo(video) {
  if (lazyVideos.has(video)) return;
  const state = { visible: false, controls: video.controls };
  lazyVideos.set(video, state);
  video.autoplay = false;
  video.preload = 'none';
  syncVideo(video, state);
  if (videoObserver) videoObserver.observe(video);
  else video.controls = true;
}

function syncVideos() {
  for (const [video, state] of lazyVideos) {
    if (!video.isConnected) {
      video.pause();
      videoObserver?.unobserve(video);
      lazyVideos.delete(video);
    } else syncVideo(video, state);
  }
}

document.addEventListener('visibilitychange', syncVideos);
reduceMotion.addEventListener('change', syncVideos);
document.addEventListener('shopify:section:unload', (event) => {
  for (const video of event.target.querySelectorAll('video.byms-lazy-video')) {
    video.pause();
    videoObserver?.unobserve(video);
    lazyVideos.delete(video);
  }
});

function prepareLazyVideos(scope = document) {
  if (!scope?.querySelectorAll) return;
  scope.querySelectorAll('video.byms-lazy-video').forEach(prepareVideo);
}

const preparedHeroVideos = new WeakSet();

function prepareHeroVideo(video) {
  if (preparedHeroVideos.has(video)) return;
  preparedHeroVideos.add(video);

  if (reduceMotion.matches || saveData || !window.matchMedia('(min-width: 750px)').matches) return;

  const source = video.querySelector('source[data-src]');
  if (!source) return;

  let started = false;
  let fallbackTimer;

  const start = () => {
    if (started) return;
    started = true;
    window.clearTimeout(fallbackTimer);
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
    video.addEventListener('canplay', () => video.classList.add('is-ready'), { once: true });
    video.load();
    video.play().catch(() => {});
  };

  ['pointerdown', 'keydown', 'scroll'].forEach((eventName) => {
    window.addEventListener(eventName, start, { once: true, passive: eventName !== 'keydown' });
  });

  const scheduleFallback = () => {
    fallbackTimer = window.setTimeout(start, 8000);
  };

  if (document.readyState === 'complete') scheduleFallback();
  else window.addEventListener('load', scheduleFallback, { once: true });
}

function prepareHeroVideos(scope = document) {
  if (!scope?.querySelectorAll) return;
  scope.querySelectorAll('video[data-byms-hero-video]').forEach(prepareHeroVideo);
}

prepareLazyVideos();
prepareHeroVideos();
document.addEventListener('shopify:section:load', (event) => {
  prepareLazyVideos(event.target);
  prepareHeroVideos(event.target);
});
